import { useEffect, useRef } from "react";

import { useDispatch, useSelector } from "react-redux";

import useLatestValue from "app/custom-hooks/useLatestValue";
import type { MdComponentProps } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/lead-generator/types/mdComponentProps";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters";
import { leadsFilterDtoToPanelValue } from "app/pages/sales-intelligence/modules/leads-search-filters/utils/leadsFilterDtoAdapters";
import {
    addAppliedSignalsAction,
    removeAppliedSignalsAction,
} from "app/pages/sales-intelligence/sub-modules/signals/store/action-creators";
import type { IPlainObject } from "app/pages/sales-intelligence/types/plainObject";

import { setIsInitialChatState } from "../../../store";
import { selectProcessedMessageData, setContextualSuggestions, setOutputFilters } from "../store";
import type { ParsedContentCommand } from "../types/content";

import { useCreateSignalDraftFromMessage } from "./useCreateSignalDraftFromMessage";

type Params<T extends IPlainObject> = Pick<MdComponentProps<T>, "filtersManager"> & {
    agentMessageId: string;
};

export const useMessageContentProcessor = ({
    filtersManager,
    agentMessageId,
}: Params<LeadsSearchAllFiltersTypes>): void => {
    const dispatch = useDispatch();

    const lastProcessedMessageIdRef = useRef<string | null>(null);

    const processedMessageData = useSelector((state) => selectProcessedMessageData(state, agentMessageId));

    const { mutate: createSignal } = useCreateSignalDraftFromMessage(agentMessageId);

    const handleContentParsed = useLatestValue((command: ParsedContentCommand, _messageId: string) => {
        switch (command.name) {
            case "OutputFilters":
                dispatch(setOutputFilters(command.data));
                filtersManager.updateFilters(leadsFilterDtoToPanelValue(command.data));

                break;

            case "ContextualSuggestions":
                dispatch(setContextualSuggestions(command.data));

                break;

            case "SuggestedQueries":
                dispatch(setIsInitialChatState(true));

                break;

            case "CraftedSignal": {
                createSignal(command.data);

                break;
            }

            case "SignalsFiltersWereModified":
            case "SignalsFiltersWereAdded": {
                const { signalIds } = command.data;

                if (signalIds.length > 0) {
                    dispatch(addAppliedSignalsAction(signalIds));
                }

                break;
            }

            case "SignalsFiltersWereRemoved": {
                const { signalIds } = command.data;

                if (signalIds.length > 0) {
                    dispatch(removeAppliedSignalsAction(signalIds));
                }

                break;
            }

            default:
                break;
        }
    });

    useEffect(() => {
        if (!processedMessageData?.commands || lastProcessedMessageIdRef.current === processedMessageData.messageId) {
            return;
        }

        processedMessageData.commands.forEach((command: ParsedContentCommand) => {
            handleContentParsed.current(command, processedMessageData.messageId);
        });

        lastProcessedMessageIdRef.current = processedMessageData.messageId;
    }, [processedMessageData?.messageId, processedMessageData?.commands]);
};
